import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Ear, ShieldCheck } from '@phosphor-icons/react';

export default function ConsentPage() {
  const { user, logout } = useAuth();
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Prend en compte les consentements déjà acceptés par ce user
  // (par exemple s'il avait déjà accepté la politique v1.0 avant l'ajout des CGV).
  const alreadyPrivacy = !!user?.privacy_accepted;
  const alreadyCgv = !!user?.cgv_accepted;

  const handleAccept = async () => {
    // Détermine ce qu'il reste à accepter et ce que l'utilisateur a coché
    const toSend = [];
    if (!alreadyPrivacy && privacyAccepted) toSend.push('privacy');
    if (!alreadyCgv && cgvAccepted) toSend.push('cgv');

    // Blocage : on doit avoir accepté tout ce qui manquait
    const stillMissingPrivacy = !alreadyPrivacy && !privacyAccepted;
    const stillMissingCgv = !alreadyCgv && !cgvAccepted;
    if (stillMissingPrivacy || stillMissingCgv) {
      toast.error("Vous devez accepter la politique de confidentialité et les CGV.");
      return;
    }
    if (toSend.length === 0) {
      // Déjà tout accepté (cas rare : URL /consent visitée alors que tout est fait)
      window.location.href = user?.role === 'client' ? '/client' : '/';
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/rgpd/consent`, { types: toSend, source: 'login_consent' });
      toast.success('Consentement enregistré');
      window.location.reload();
    } catch (err) {
      toast.error("Erreur lors de l'enregistrement du consentement.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="flex items-center justify-center gap-3 mb-8">
          <Ear size={32} weight="bold" className="text-zinc-900" />
          <h1 className="font-heading font-bold text-xl tracking-wider text-zinc-900">HAMAC SOLUTIONS</h1>
        </div>

        <div className="bg-white border border-zinc-200 rounded-sm p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-6">
            <ShieldCheck size={32} className="text-zinc-900" />
            <h2 className="font-heading font-semibold text-lg">Consentement requis</h2>
          </div>

          <p className="text-sm text-zinc-600 mb-4">
            Bonjour <strong>{user?.name}</strong>, avant de pouvoir passer commande vous devez accepter <strong>la politique de confidentialité</strong> (RGPD) ainsi que nos <strong>Conditions Générales de Vente</strong>.
          </p>

          <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-4 mb-6 max-h-48 overflow-y-auto text-xs text-zinc-600 leading-relaxed">
            <p className="font-semibold mb-2">En résumé :</p>
            <ul className="list-disc pl-4 space-y-1">
              <li>Vos données professionnelles sont utilisées pour la gestion des commandes et la fabrication</li>
              <li>Aucune donnée n'est vendue ni cédée à des tiers</li>
              <li>Vous disposez de droits d'accès, rectification, suppression et opposition</li>
              <li>La relation commerciale est régie par les CGV — paiement à 30 jours fin de mois, garantie légale des vices cachés, tribunal compétent : Bourg-en-Bresse</li>
            </ul>
          </div>

          <div className="space-y-3 mb-6">
            <div className={`border rounded-sm p-4 ${alreadyPrivacy ? 'bg-emerald-50 border-emerald-200' : 'bg-zinc-50 border-zinc-200'}`}>
              <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-privacy-label">
                <Checkbox
                  checked={alreadyPrivacy || privacyAccepted}
                  disabled={alreadyPrivacy}
                  onCheckedChange={setPrivacyAccepted}
                  className="mt-0.5"
                  data-testid="consent-privacy-checkbox"
                />
                <span className="text-sm text-zinc-700">
                  J'accepte la{' '}
                  <Link to="/confidentialite" target="_blank" className="text-zinc-900 underline font-semibold">
                    politique de confidentialité
                  </Link>
                  {alreadyPrivacy && <span className="ml-2 text-emerald-700 text-xs font-medium">✓ Déjà accepté</span>}
                </span>
              </label>
            </div>

            <div className={`border rounded-sm p-4 ${alreadyCgv ? 'bg-emerald-50 border-emerald-200' : 'bg-zinc-50 border-zinc-200'}`}>
              <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-cgv-label">
                <Checkbox
                  checked={alreadyCgv || cgvAccepted}
                  disabled={alreadyCgv}
                  onCheckedChange={setCgvAccepted}
                  className="mt-0.5"
                  data-testid="consent-cgv-checkbox"
                />
                <span className="text-sm text-zinc-700">
                  J'accepte les{' '}
                  <Link to="/cgv" target="_blank" className="text-zinc-900 underline font-semibold">
                    Conditions Générales de Vente
                  </Link>
                  {alreadyCgv && <span className="ml-2 text-emerald-700 text-xs font-medium">✓ Déjà accepté</span>}
                </span>
              </label>
            </div>
          </div>

          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => { logout(); window.location.href = '/login'; }} data-testid="consent-decline-btn">
              Refuser et se déconnecter
            </Button>
            <Button
              className="flex-1 bg-zinc-900 hover:bg-zinc-800 text-white"
              disabled={submitting || (!alreadyPrivacy && !privacyAccepted) || (!alreadyCgv && !cgvAccepted)}
              onClick={handleAccept}
              data-testid="consent-accept-btn"
            >
              {submitting ? 'Enregistrement...' : 'Accepter et continuer'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
